import { ChangeDetectorRef, Component, HostListener, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { SelectedAttributeService } from '../../services/selected-attribute.service';
import { ConfiguredItem } from 'src/app/interfaces/configured-item';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
// import { findAlternateConflicts } from 'src/app/utils/configured-item.util';
import { Navigation2Service } from '../../services/navigation2.service';
import { AttributeMenuComponent } from '../attribute-menu/attribute-menu.component';
import { AttributeMenuService } from '../../services/attribute-menu.service';
import { ResetDialogComponent } from '../reset.dialog/reset.dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics/google-analytics.service';
import { BookmarksService } from '../../services/bookmarks.service';
import { ConfiguredItemService } from 'src/app/services/configured-item/configured-item.service';
import { assert } from 'src/app/utils/assert.util';
import { CheckoutService } from '../../services/checkout.service';
import { Subscription } from 'rxjs';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { MainImageService } from 'src/app/services/main-image/main-image.service';
import { IframeService } from 'src/app/services/iframe/iframe.service';
import { PriceService } from 'src/app/services/price/price.service';
import { environment } from 'src/environments/environment';
import { CheckoutSummaryComponent } from '../checkout-summary/checkout-summary.component';
import { ThumbnailProviderService } from 'src/app/providers/thumbnail/thumbnail.provider.service';

@Component({
  selector: 'app-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss']
})
export class ToolbarComponent {
  private priceService = inject(PriceService);
  private iframeService = inject(IframeService);
  private mainImageService = inject(MainImageService);
  private loadingService = inject(LoadingService);
  private configuredItemService = inject(ConfiguredItemService);
  private bookmarksService = inject(BookmarksService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private checkoutService = inject(CheckoutService);
  private thumbnailProvider = inject(ThumbnailProviderService);
  canRetailerAddToCart = input(false);
  skuToShow = signal('');
  selectedAttributes = computed(() => this.selectedAttributeService.getValue());
  actualSelectedAttributes = computed(() => Object.keys(this.selectedAttributes()).reduce((obj, key) => {
    if (key === 'primaryMaterial') {
      obj['Material'] = [this.selectedAttributes().primaryMaterial, this.selectedAttributes().secondaryMaterial].filter(m => m).join(', ');
      return obj;
    }
    if (['engravingType','engravingText', 'secondaryMaterial'].includes(key)) {
      return obj;
    }
    const keyArr = key.split('');
    keyArr[0] = keyArr[0].toUpperCase();
    const newKey = keyArr.join('');
    obj[newKey] = this.selectedAttributes()[key as keyof(ConfiguredItem)];
    return obj;
  }, {} as any));
  private b2bDataSubscription: Subscription | null = null;
  selectedAttributeCount = computed(() => Object.keys(this.actualSelectedAttributes()).filter(k => this.actualSelectedAttributes()[k]).length);
  customSKU = computed(() => this.configuredItemService.getNewSKUFromSelectedAttributes(this.selectedAttributeService.configuredItem()));
  bookmarkKey = computed(() => {
    const sku = this.customSKU();
    const haloSKU = !this.configuredItemService.isCustomSKU(this.skuToShow()) && !this.configuredItemService.isCustomStyle(this.skuToShow()) ? this.skuToShow() : '';
    return `${sku}:${haloSKU}`;
  });
  bookmarks = computed(() => this.bookmarksService.bookmarks());
  shouldShowBackButton = false;
  backButtonAnimationDirection: 'In' | 'Out' | null = null;
  shouldShowNextButton = false;
  nextButtonAnimationDirection: 'In' | 'Out' | null = null;
  // conflictCount = computed(() => this.selectedAttributeService.conflicts().length);
  // conflictText = computed(() => `${this.conflictCount()} Conflict${this.conflictCount() > 1 ? 's' : ''}`);
  conflictCount: number = 0;
  conflictText: string | null = null;
  shouldShowConflictButton = false;
  conflictButtonAnimationDirection: 'In' | 'Out' | null = null;
  shouldButtonsAnimate = false;
  currentViewTitle = signal<string | null>(null);
  canUndo = computed(() => this.selectedAttributeService.undoList().length > 0);
  canRedo = computed(() => this.selectedAttributeService.redoList().length > 0);
  isReviewing = signal(false);
  isRetailOnlyMode = computed(() => this.checkoutService.isRetailerOnly());
  shouldShowPrice = computed(() => this.navigationService.activeView().title !== 'Retailer Summary');
  retailPrice = computed(() =>  this.priceService.retailPrice());
  wholesalePriceToShow = computed(() => this.priceService.wholesalePriceToShow());
  shouldShowWholesalePrice = signal<boolean>(false);
  /** Guards against a second add-to-cart while the first is waiting on the thumbnail (ADO #8902). */
  private isAddingToCart = false;

  constructor(
    private selectedAttributeService: SelectedAttributeService,
    private navigationService: Navigation2Service,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
  ) {
    // this.selectedAttributeService.subscribeToConflictsChange((conflicts) => {
    //   if (conflicts.length > 0) {
    //     this.resolveClick();
    //   }
    // });

    // this.navigationService.subscribeActiveViewIndexChange(() => {
    //   this.shouldButtonsAnimate = true;
    //   this.currentViewTitle.set(this.navigationService.activeView.title);
    // });

    effect(() => {
      this.shouldButtonsAnimate = true;
      this.currentViewTitle.set(this.navigationService.activeView().title);
    }, {allowSignalWrites: true})

    this.navigationService.subscribe((action) => {
      if (action.data.title === 'Review') {
        if (action.action === 'Present') {
          this.isReviewing.set(true);
        } else if (action.action === 'Dismiss') {
          this.isReviewing.set(false);
        }
      }
    });

    effect(async () => {
      this.loadingService.load('previewer::price');
      const pricingObject = this.selectedAttributeService.pricingObject();
      if (pricingObject) {
        await this.priceService.update(pricingObject, untracked(() => this.selectedAttributeService.configuredItem()));
      }
      this.loadingService.done('previewer::price');
    }, { allowSignalWrites: true });
  }

  ngOnInit() {
    const size = this.selectedAttributeService.getValue().size;
    assert(size, 'No size selected');
    this.shouldShowWholesalePrice.set(!this.checkoutService.b2bData.retailPricingOnly);
    this.skuToShow.set((!this.checkoutService.b2bData.haloStyle ? undefined : this.configuredItemService.buildHaloSKU(this.checkoutService.b2bData.haloStyle, size.value, false)) ?? this.configuredItemService.getNewSKUFromSelectedAttributes(this.selectedAttributeService.configuredItem()));
    this.b2bDataSubscription = this.checkoutService.subscribeToB2BDataChanges(async value => {
      const size = this.selectedAttributeService.getValue().size;
      assert(size, 'No size selected');
      this.loadingService.load('SKUToShow');
      this.skuToShow.set((!value.haloStyle ? undefined : this.configuredItemService.buildHaloSKU(value.haloStyle, size.value, false)) ?? this.configuredItemService.getNewSKUFromSelectedAttributes(this.selectedAttributeService.configuredItem()));
      this.loadingService.done('SKUToShow');
    });
  }

  // resolveClick() {
  //   const resolutionRef: MatDialogRef<ConflictResolutionDialogComponent, { key: string, value: string }[]> = this.dialog.open(ConflictResolutionDialogComponent, { maxWidth: 500, disableClose: true, autoFocus: false, data: { currentConflictingAttributes: this.selectedAttributeService.getConflicts(), alternateConflictingAttributes: findAlternateConflicts(this.selectedAttributeService.getConflicts(), this.selectedAttributeService.getValue()) } });
  //   resolutionRef.afterClosed().subscribe(this.resolutionAfterClose.bind(this));
  // }

  reset() {
    this.triggerGAEvent('click', 'reset', '1');
    this.dialog.open(ResetDialogComponent, { disableClose: true }).afterClosed().subscribe((v) => {
      if (v) {
        this.selectedAttributeService.reset();
      }
    })
  }

  async undo() {
    this.triggerGAEvent('click', 'undo', '1');
    if (this.canUndo()) {
      await this.selectedAttributeService.undo();
    }
  }

  async redo() {
    this.triggerGAEvent('click', 'redo', '1');
    if (this.canRedo()) {
      await this.selectedAttributeService.redo();
    }
  }

  @HostListener('animationend', ['$event.target'])
  viewContainerAnimationEnd(target: HTMLElement) {
    if (target.classList.contains('BackButton')) {
      if (this.backButtonAnimationDirection === 'Out') {
        this.shouldShowBackButton = false;
      }
      this.backButtonAnimationDirection = null
    }

    if (target.classList.contains('NextButton')) {
      if (this.nextButtonAnimationDirection === 'Out') {
        this.shouldShowNextButton = false;
      }
      this.nextButtonAnimationDirection = null
    }

    if (target.classList.contains('ConflictButton')) {
      if (this.conflictButtonAnimationDirection === 'Out') {
        this.shouldShowConflictButton = false;
      }
      this.conflictButtonAnimationDirection = null
    }
  }

  shareClick() {
    this.snackBar.open('Share URL copied to clipboard!', undefined, { duration: 1000 })
  }

    triggerGAEvent(action: string, title: string, value: string) {
    if(this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent(action, 'toolbar',  title, value);
    }
  }

  async toggleBookmark() {
    this.loadingService.load('bookmarks::toggle');
    if (this.bookmarks().some(b => b.sku === this.bookmarkKey())) {
      await this.bookmarksService.remove(this.bookmarkKey());
    } else {
      const thumbnail = await this.mainImageService?.getSnapshot?.call(this.mainImageService);
      assert(thumbnail, 'Could not get favorite thumbnail');
      await this.bookmarksService.add(this.bookmarkKey(), thumbnail);
    }
    this.loadingService.done('bookmarks::toggle');
  }

  isBookmarkKeyInBookmarks(bookmarkKey: string) {
    return this.bookmarks().some(b => b.sku === bookmarkKey);
  }

  async reviewClicked() {
    if (!this.isReviewing()) {
      this.summaryClick()
    } else if (this.isRetailOnlyMode()) {
      // This component is the mobile control and sits at z-index 6, above the loading overlay, so
      // the overlay never gated this button at all. Awaiting is the gate (ADO #8902).
      if (this.isAddingToCart) {
        return;
      }
      this.isAddingToCart = true;
      this.loadingService.load('toolbar:add-to-cart');
      try {
        await this.thumbnailProvider.whenSubmissionSettled();
        const engravingText = this.selectedAttributeService.configuredItem().engravingText;
        const engravingType = this.selectedAttributeService.configuredItem().engravingType
        this.iframeService.sendMessage(JSON.stringify({
          type: 'Benchmark::RingLab::addtocart',
          value: {
            sku: this.skuToShow(),
            engravingType: engravingType && engravingText ? engravingType.displayName : null,
            engravingText: engravingType && engravingText ? engravingText : null,
          }
        }));
      } finally {
        this.loadingService.done('toolbar:add-to-cart');
        this.isAddingToCart = false;
      }
    } else {
      this.addToCartClick();
    }
  }

  summaryClick() {
    this.triggerGAEvent('click', 'menu_item', 'Review');
    this.navigationService.present({ title: 'Review', type: CheckoutSummaryComponent, doesAppearInProgressBar: true });
  }

  async addToCartClick() {
    try {
      this.loadingService.load('checkout-summary:add-to-cart');
      const pricingObject = this.selectedAttributeService.pricingObject();
      assert(pricingObject, 'Pricing object was null');
      await this.priceService.update(pricingObject, this.selectedAttributeService.configuredItem());
      const priceToSend = this.priceService.wholesalePrice();
      assert(priceToSend, 'No price');
      const b2bData = await this.checkoutService.getOutputData(this.selectedAttributeService.configuredItem(), this.selectedAttributeService.poNumber(), this.priceService.isPricedItemQuarterSized());
      const roundedPrice = Math.round((priceToSend.valueBeforeFee + Number.EPSILON) * 100) / 100
      if (b2bData.unitPrice !== roundedPrice) {
        b2bData.unitPrice = roundedPrice;
        console.log('price updated')
      }

      const styleNumber = this.configuredItemService.getNewStyleFromSelectedAttributes(this.selectedAttributeService.configuredItem());

      if (this.googleAnalyticsService.isInitialized) {
        this.googleAnalyticsService.triggerEvent('click', 'Review', 'add_to_cart', `${styleNumber} sz: ${this.selectedAttributeService.configuredItem().size?.value ?? 'None'}`);
      }
      const payload = JSON.stringify(b2bData);
      const encodedPayload = btoa(payload);
      // Navigating away can abort an in-flight thumbnail upload outright, not merely race it, so
      // wait for it to settle before leaving the page (ADO #8902).
      await this.thumbnailProvider.whenSubmissionSettled();
      window.location.href = `${environment.b2bURL}/customitem?o=${encodedPayload}`;
    } catch (e) {
      console.error(e);
      this.loadingService.done('checkout-summary:add-to-cart');
    }
  }
}
